import React, { ReactNode, useEffect, useState } from 'react';
import { useResponsive } from '../hooks/useResponsive';

interface ResponsiveContainerProps {
  children: ReactNode;
}

const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({ children }) => {
  const { isMobile, isTablet } = useResponsive();
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    pointerEvents: 'auto',
    backgroundColor: '#1e293b'
  };
  
  const controlsStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: isMobile ? '10px' : '20px',
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'auto',
    zIndex: 10
  };
  
  const infoStyle: React.CSSProperties = {
    position: 'absolute',
    top: isMobile ? '10px' : '20px',
    right: isMobile ? '10px' : '20px',
    padding: isMobile ? '5px 10px' : '10px 15px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    borderRadius: '5px',
    fontSize: isMobile ? '12px' : '14px',
    zIndex: 10
  };

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  // Handle orientation change
  useEffect(() => {
    const handleOrientationChange = () => {
      if (window.orientation === 90 || window.orientation === -90) {
        // Landscape mode
        if (isMobile && !isFullscreen) {
          toggleFullscreen();
        }
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobile, isFullscreen]);
  
  return (
    <div style={containerStyle}>
      {children}
      
      {/* Mobile rotation message */}
      {isMobile && window.innerHeight > window.innerWidth && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '20px' }}>📱 ↔️</div>
          <h2 style={{ marginBottom: '10px' }}>Please rotate your device</h2>
          <p>For the best experience, use landscape orientation.</p>
        </div>
      )}

      {/* Fullscreen toggle button */}
      {isMobile && (
        <button
          onClick={toggleFullscreen}
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            padding: '8px 12px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            zIndex: 1000,
            fontSize: '14px'
          }}
        >
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
      )}
    </div>
  );
};

export default ResponsiveContainer; 